import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

const ThemeContext = createContext({
  themeMode: "auto", // "light" | "dark" | "auto"
  themeAccent: "default", // e.g. "blue" | "green" | "rose"
  effectiveMode: "light", // resolved "light" | "dark"
  setThemeMode: () => {},
  setThemeAccent: () => {},
});

const LSTORAGE_KEY = "app-theme-pref"; // ONLY localStorage mirror (no Dexie here)
const DEFAULT_ACCENT = "default";
const DEFAULT_MODE = "auto";

/**
 * Determine the effective theme mode to apply.
 *
 * If `themeMode` is the literal string "auto", this function attempts to detect the user's
 * system preference using the `prefers-color-scheme: dark` media query:
 * - If `window` and `window.matchMedia` are available and the media query matches, returns "dark".
 * - If the media query does not match or the environment does not support `window`/`matchMedia`, returns "light".
 *
 * For any `themeMode` value other than "auto" (for example "dark" or "light"), the original value is returned unchanged.
 *
 * @param {'auto'|'dark'|'light'|string} themeMode - The requested theme mode; "auto" defers to the system preference.
 * @returns {'dark'|'light'|string} The resolved effective theme: "dark" or "light" when auto is used, otherwise the provided value.
 */
function resolveEffectiveMode(themeMode) {
  if (themeMode !== "auto") return themeMode;
  const prefersDark =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  return prefersDark ? "dark" : "light";
}

/**
 * Normalize a theme mode string to one of the supported values.
 * The valid values is a small, fixed enum — "light" | "dark" | "auto".
 * “Normalize” here means: if it’s one of those three, keep it;
 * otherwise coerce to a safe default ("auto").
 *
 * This function is case-sensitive: it returns the input when it is exactly "dark" or "light".
 * For any other value (including undefined or null) it returns "auto".
 *
 * @param {string} m - The input mode to normalize.
 * @returns {"dark"|"light"|"auto"} The normalized theme mode: "dark", "light", or "auto".
 */
function normalizeMode(m) {
  return m === "dark" || m === "light" ? m : "auto";
}

/**
 * Validate and normalize an accent value. The accent is an open-ended token
 * (any string key you define in CSS, e.g., "blue", "orange-500", "brandA").
 * “Sanitize” here means: ensure it’s a non-empty string; otherwise fall back
 * to a default (e.g., "default").
 *
 * Trims whitespace and returns the original value when it is a non-empty string.
 * If the input is not a string or is empty after trimming, returns DEFAULT_ACCENT.
 *
 * @param {*} x - The value to sanitize as an accent.
 * @returns {string} The validated accent string, or DEFAULT_ACCENT when the input is invalid.
 */
function sanitizeAccent(a) {
  return typeof a === "string" && a.trim().length > 0 ? a : DEFAULT_ACCENT;
}

// getBootTheme(): ensure a real fallback
/**
 * Determine the initial theme settings used to bootstrap the UI.
 *
 * Priority of sources:
 * 1. Attempts to read and parse localStorage.getItem("app-theme-pref") which is expected
 *    to be a JSON object with keys { themeMode, themeAccent }.
 * 2. If localStorage is unavailable or parsing fails, and `document` exists, reads the
 *    HTML root element attributes `data-theme` and `data-theme-accent`.
 * 3. Falls back to defaults when neither source yields valid values.
 *
 * Validation and defaults:
 * - themeMode: if the source value is "dark" or "light" it is returned as-is; otherwise "auto".
 * - themeAccent: uses the provided value when present; otherwise defaults to "default".
 *
 * Note: errors during localStorage access or JSON parsing are caught and do not propagate.
 *
 * @returns {{themeMode: "dark" | "light" | "auto", themeAccent: string}}
 *          An object containing the resolved themeMode and themeAccent for bootstrapping.
 */
function getBootTheme() {
  try {
    // return values from local storage which mirrors indexeddb
    const raw = localStorage.getItem("app-theme-pref");
    if (raw) {
      const { themeMode, themeAccent } = JSON.parse(raw);
      return {
        themeMode:
          themeMode === "dark" || themeMode === "light" ? themeMode : "auto",
        themeAccent: themeAccent ?? "default", // <- nullish coalesce
      };
    }
  } catch {}

  // problem with localstorage, try reading the html element attributes
  // return those values or defaults
  if (typeof document !== "undefined") {
    const html = document.documentElement;
    const attrMode = html.getAttribute("data-theme");
    const attrAccent = html.getAttribute("data-theme-accent");
    return {
      themeMode:
        attrMode === "dark" || attrMode === "light" ? attrMode : "auto",
      themeAccent: attrAccent ?? "default", // <- fallback
    };
  }

  // html document is not defined, return hard coded defaults
  return { themeMode: "auto", themeAccent: "default" };
}

// previous
// function getBootTheme() {
//   // 1) Try localStorage (fast, no async)
//   try {
//     const raw = localStorage.getItem(LSTORAGE_KEY);
//     if (raw) {
//       const { themeMode, themeAccent } = JSON.parse(raw);
//       return {
//         themeMode: normalizeMode(themeMode) ?? "auto",
//         themeAccent: themeAccent || "blue",
//       };
//     }
//   } catch {}
//   // 2) Fall back to current HTML attributes, if present
//   if (typeof document !== "undefined") {
//     const html = document.documentElement;
//     const attrMode = html.getAttribute("data-theme"); // often "light"/"dark"
//     const attrAccent = html.getAttribute("data-theme-accent");
//     const startMode =
//       attrMode === "dark" || attrMode === "light" ? attrMode : "auto";
//     return { themeMode: startMode, themeAccent: attrAccent || "blue" };
//   }
//   // 3) Defaults
//   return { themeMode: "auto", themeAccent: "blue" };
// }

/**
 * Persist the user's theme settings to localStorage.
 *
 * The function normalizes the provided mode using `normalizeMode` and
 * sanitizes the provided accent using `sanitizeAccent`, then stores a JSON
 * payload under the `LSTORAGE_KEY` key with the shape:
 *   { themeMode: <normalizedMode>, themeAccent: <sanitizedAccent> }
 *
 * Any errors thrown while accessing localStorage (for example, when running
 * in a restricted environment or if the storage quota is exceeded) are caught
 * and suppressed to avoid throwing from the caller.
 *
 * @param {string} mode - Theme mode to persist (e.g. "light" or "dark"); will be passed to `normalizeMode`.
 * @param {string} accent - Accent identifier (name or color); will be passed to `sanitizeAccent`.
 * @returns {void}
 * @see normalizeMode
 * @see sanitizeAccent
 */

// removed after adding effect that verifies local storage key exists on first load
// function mirrorToLocalStorage(mode, accent) {
//   try {
//     localStorage.setItem(
//       LSTORAGE_KEY,
//       JSON.stringify({
//         themeMode: normalizeMode(mode),
//         themeAccent: sanitizeAccent(accent),
//       })
//     );
//   } catch {}
// }

// troubleshooting/
// new MutationObserver((mutations) => {
//   for (const m of mutations) {
//     if (m.attributeName === "data-theme-accent") {
//       console.log(
//         "data-theme-accent ->",
//         document.documentElement.getAttribute("data-theme-accent")
//       );
//     }
//   }
// }).observe(document.documentElement, { attributes: true });

/**
 * ThemeProvider
 *
 * Execution order (mount & updates)
 * --------------------------------
 * - Module import time:
 *   - Module-scoped helpers and constants are evaluated/declared (e.g. normalizeMode, sanitizeAccent,
 *     resolveEffectiveMode, getBootTheme, mirrorToLocalStorage, LSTORAGE_KEY). These are not part of the component
 *     render cycle but are used by it.
 *
 * - Render phase (runs on initial render and on each re-render):
 *   1. getBootTheme() is invoked (used to bootstrap initial UI state and avoid theme flash).
 *   2. useState initializers run:
 *      - themeMode initialized from boot.themeMode
 *      - themeAccent initialized from lazy initializer (defaults to "default")
 *      - effectiveMode initialized from resolveEffectiveMode(boot.themeMode)
 *   3. useCallback hooks are created for setThemeMode and setThemeAccent.
 *   4. useMemo computes the context value object.
 *
 * - Commit phase (after DOM paint — all useEffect callbacks execute in source order):
 *   1. Effect: recompute effectiveMode when themeMode changes (dependency: [themeMode]).
 *   2. Effect: listen to OS color-scheme changes via matchMedia (dependency: [themeMode]); attaches/detaches
 *      listeners only on mount/unmount or when themeMode changes.
 *   3. Effect: reflect effectiveMode and themeAccent onto the <html> element (dependency: [effectiveMode, themeAccent]).
 *   4. Effect: attach a 'storage' event listener for cross-tab sync (dependency: [] — runs once on mount and
 *      removes on unmount).
 *
 * Notes on updates:
 * - Effects run after each commit and only when their dependency arrays indicate changes.
 * - State updates performed inside effects schedule additional renders; effects themselves are invoked in the
 *   same order they appear in source.
 *
 * Overview
 * --------
 * A React context provider that:
 * - Manages theme preference state: themeMode ("light" | "dark" | "auto") and themeAccent (string token).
 * - Maintains effectiveMode ("light" | "dark") which resolves "auto" against the OS preference.
 * - Reflects effectiveMode and sanitized themeAccent to document.documentElement attributes.
 * - Persists choices to localStorage and synchronizes across tabs/windows via the 'storage' event.
 * - Guards against SSR by checking for window/document availability.
 *
 * Public context API (value)
 * - themeMode: "light" | "dark" | "auto" — the chosen preference
 * - themeAccent: string — sanitized accent token (never undefined)
 * - effectiveMode: "light" | "dark" — resolved mode used for UI
 * - setThemeMode(next: string): void — normalizes and sets themeMode, updates effectiveMode and mirrors to localStorage
 * - setThemeAccent(next: string|null|undefined): void — sanitizes and sets themeAccent and mirrors to localStorage
 *
 * Invariants & behavior guarantees
 * - Inputs are normalized/sanitized using module-scoped helpers (normalizeMode, sanitizeAccent) so invalid values
 *   are not stored or written to the DOM.
 * - mirrorToLocalStorage writes a JSON payload under LSTORAGE_KEY; the storage event handler reads that payload
 *   and updates local state in other tabs.
 * - The storage listener effect intentionally uses an empty dependency array so it is attached exactly once on mount.
 * - matchMedia listeners are only active when themeMode === "auto" to avoid unnecessary global listeners.
 * - SSR safe: effects checking typeof window/document avoid running in non-browser environments.
 *
 * Usage
 * -----
 * Wrap your app with <ThemeProvider>{children}</ThemeProvider> and consume the ThemeContext to access the API.
 *
 * @component
 * @param {{ children: import('react').ReactNode }} props
 * @returns {import('react').ReactElement} ThemeContext.Provider containing theme state and setters
 */
export function ThemeProvider({ children }) {
  // Boot instantly from localStorage/HTML attrs to avoid theme flash
  const boot = getBootTheme();
  const [themeMode, _setThemeMode] = useState(boot.themeMode);
  const [themeAccent, _setThemeAccent] = useState(boot.themeAccent);
  // const [themeAccent, _setThemeAccent] = useState(
  //   () => /* "default" bootstrap */ "default"
  // );
  const [effectiveMode, setEffectiveMode] = useState(
    resolveEffectiveMode(boot.themeMode)
  );

  // Recompute effective mode whenever the chosen mode changes
  useEffect(() => {
    setEffectiveMode(resolveEffectiveMode(themeMode));
  }, [themeMode]);

  // Listen to OS changes only when in "auto"
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => {
      // e.matches is true when the query matches (dark mode)
      if (themeMode === "auto") {
        setEffectiveMode(e.matches ? "dark" : "light");
      }
    };

    if (typeof mql.addEventListener === "function") {
      // Modern browsers
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    } else if (typeof mql.addListener === "function") {
      // Legacy Safari (<14)
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
  }, [themeMode]);

  // previous with deprecated listeners
  // useEffect(() => {
  //   if (typeof window === "undefined" || !window.matchMedia) return;
  //   const mql = window.matchMedia("(prefers-color-scheme: dark)");
  //   const onChange = () => {
  //     if (themeMode === "auto") {
  //       setEffectiveMode(resolveEffectiveMode("auto"));
  //     }
  //   };
  //   mql.addEventListener?.("change", onChange);
  //   mql.addListener?.(onChange); // Safari < 14
  //   return () => {
  //     mql.removeEventListener?.("change", onChange);
  //     mql.removeListener?.(onChange);
  //   };
  // }, [themeMode]);

  // reflect to <html>: never write undefined
  useEffect(() => {
    if (typeof document === "undefined") return;
    const html = document.documentElement;

    const accent = sanitizeAccent(themeAccent); // <- guarantees a string
    html.setAttribute("data-theme", effectiveMode);
    html.setAttribute("data-theme-accent", accent);
  }, [effectiveMode, themeAccent]);

  // previous
  // Reflect state to <html> attributes + helpful meta tags
  // useEffect(() => {
  //   if (typeof document === "undefined") return;
  //   const html = document.documentElement;

  //   html.setAttribute("data-theme", effectiveMode); // "light" | "dark"
  //   html.setAttribute("data-theme-accent", themeAccent);

  //   // Hint to UA which schemes we support so native controls style correctly
  //   let colorSchemeMeta = document.querySelector('meta[name="color-scheme"]');
  //   if (!colorSchemeMeta) {
  //     colorSchemeMeta = document.createElement("meta");
  //     colorSchemeMeta.setAttribute("name", "color-scheme");
  //     document.head.appendChild(colorSchemeMeta);
  //   }
  //   colorSchemeMeta.setAttribute(
  //     "content",
  //     effectiveMode === "dark" ? "dark light" : "light dark"
  //   );

  //   // Optional neutral theme-accent meta (adjust if you map brand colors)
  //   let themeMeta = document.querySelector('meta[name="theme-accent"]');
  //   if (!themeMeta) {
  //     themeMeta = document.createElement("meta");
  //     themeMeta.setAttribute("name", "theme-accent");
  //     document.head.appendChild(themeMeta);
  //   }
  //   themeMeta.setAttribute(
  //     "content",
  //     effectiveMode === "dark" ? "#0b0b0d" : "#ffffff"
  //   );
  // }, [effectiveMode, themeAccent]);

  /**
   * Cross-tab theme sync via `storage` event.
   *
   * WHAT:
   *   Listens for changes to the browser's `localStorage` key "app-theme-pref"
   *   and updates the in-memory theme state so all open tabs/windows stay in sync.
   *
   * WHY:
   *   - When a user changes theme in one tab, other tabs won’t know unless we
   *     listen for the `storage` event (which fires in *other* documents).
   *   - Keeps `<html data-theme="…">` and `data-theme-accent` consistent everywhere.
   *
   * EVENT DETAILS:
   *   - Fires only in *other* tabs/windows of the same origin when `localStorage.setItem`
   *     (or remove/clear) is called. It does NOT fire in the same tab that made the change.
   *   - Handler receives `StorageEvent` with:
   *       e.key        → changed key (string | null)
   *       e.newValue   → new value (string | null) AFTER the change
   *       e.oldValue   → previous value (string | null) BEFORE the change
   *       e.storageArea→ the Storage object (localStorage/sessionStorage)
   *       e.url        → page URL that triggered the change
   *
   * DATA FORMAT (value of "app-theme-pref"):
   *   JSON.stringify({
   *     themeMode: "light" | "dark" | "auto",
   *     themeAccent: string   // non-empty token, e.g. "blue", "orange-500"
   *   })
   *
   * RULES OF HOOKS / DEPS:
   *   - Empty dependency array `[]` on purpose: we want to attach exactly once on mount,
   *     detach on unmount. We only call React state setters inside, which are stable.
   *   - Helpers `normalizeMode` / `normalizeAccent` live at module scope → stable as well.
   *   - We do NOT capture `themeMode`/`themeAccent` inside the handler; we derive fresh
   *     values from the event payload, so no re-subscription churn is needed.
   *
   * SAFETY & HARDENING:
   *   - Guard for unrelated keys or missing values.
   *   - Try/catch JSON parsing; ignore malformed payloads.
   *   - Coerce inputs with `normalizeMode` / `normalizeAccent` so we never write invalid state.
   *
   * SSR / NON-BROWSER:
   *   - The effect is a no-op during SSR; it runs only in the browser after mount.
   *
   * TESTING TIPS:
   *   - Open two tabs to the same app. In tab A, execute:
   *       localStorage.setItem("app-theme-pref", JSON.stringify({ themeMode: "dark", themeAccent: "blue" }));
   *     Tab B should update its theme immediately (no reload).
   *
   * EXTENSIBILITY:
   *   - If you add more theme fields, include them in the JSON and extend the handler’s
   *     normalization before calling the appropriate setters.
   */
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== LSTORAGE_KEY || !e.newValue) return;
      try {
        const { themeAccent: a, themeMode: m } = JSON.parse(e.newValue);
        if (m) _setThemeMode(normalizeMode(m));
        if (a) _setThemeAccent(sanitizeAccent(a));
      } catch {}
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // useEffect(() => {
  //   const onStorage = (e) => {
  //     if (e.key !== LSTORAGE_KEY || !e.newValue) return;
  //     try {
  //       const { themeMode: m, themeAccent: a } = JSON.parse(e.newValue);
  //       if (m && m !== themeMode) _setThemeMode(normalizeMode(m));
  //       if (a && a !== themeAccent) _setThemeAccent(a);
  //     } catch {}
  //   };
  //   window.addEventListener("storage", onStorage);
  //   return () => window.removeEventListener("storage", onStorage);
  // }, [themeMode, themeAccent]);

  // Public setters (no Dexie here): update state + mirror to localStorage
  const setThemeMode = useCallback(
    (next) => {
      const normalized = normalizeMode(next);
      _setThemeMode(normalized);
      setEffectiveMode(resolveEffectiveMode(normalized));
      // mirrorToLocalStorage(normalized, themeAccent);
      // NOTE: Dexie persistence happens in ThemePrefsSync, not here.
    },
    [themeAccent]
  );

  // setters: use nullish coalescing so empty strings don't get turned into "blue" accidentally
  /**
   * Sets the theme accent color and persists it to localStorage.
   *
   * This memoized callback function updates the theme accent state and mirrors
   * the change to localStorage for persistence across sessions.
   *
   * @param {string|null|undefined} next - The new accent color value.
   *                                       If null or undefined, defaults to "default"
   *
   * @example
   * setThemeAccent("blue");     // Sets accent to "blue"
   * setThemeAccent(null);       // Sets accent to "default"
   * setThemeAccent(undefined);  // Sets accent to "default"
   *
   * @dependencies
   * - themeMode: The callback recreates when themeMode changes, ensuring
   *              the correct theme mode is mirrored to localStorage
   *
   * @sideEffects
   * - Logs the incoming value to console
   * - Updates theme accent state via _setThemeAccent
   * - Writes to localStorage via mirrorToLocalStorage
   */
  const setThemeAccent = useCallback(
    (next) => {
      const val = sanitizeAccent(next);
      _setThemeAccent(val);
      // mirrorToLocalStorage(themeMode, val);
    },
    [themeMode]
  );

  // const setThemeAccent = useCallback(
  //   (next) => {
  //     const val = next ?? "default"; // <- never undefined
  //     _setThemeAccent(val);
  //     mirrorToLocalStorage(themeMode, val);
  //   },
  //   [themeMode]
  // );

  // previous
  // const setThemeAccent = useCallback(
  //   (next) => {
  //     const val = next || "blue";
  //     _setThemeAccent(val);
  //     mirrorToLocalStorage(themeMode, val);
  //     // NOTE: Dexie persistence happens in ThemePrefsSync (Option 2), not here.
  //   },
  //   [themeMode]
  // );

  const value = useMemo(
    () => ({
      themeMode, // "light" | "dark" | "auto"
      themeAccent, // palette key
      effectiveMode, // "light" | "dark"
      setThemeMode,
      setThemeAccent,
    }),
    [themeMode, themeAccent, effectiveMode, setThemeMode, setThemeAccent]
  );

  useEffect(() => {
    try {
      const payload = JSON.stringify({
        themeMode,
        themeAccent,
      });
      if (localStorage.getItem(LSTORAGE_KEY) !== payload) {
        localStorage.setItem(LSTORAGE_KEY, payload);
      }
    } catch (err) {
      console.warn("localStorage unavailable", err);
    }
  }, [themeMode, themeAccent]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);

// previous
// import React, {
//   createContext,
//   useContext,
//   useEffect,
//   useMemo,
//   useRef,
//   useState,
// } from "react";

// // Shape of the context so consumers don’t explode before provider mounts
// const ThemeContext = createContext({
//   themeMode: "auto", // "light" | "dark" | "auto"
//   themeAccent: "blue", // e.g. "blue" | "green" | "rose" | etc.
//   setThemeMode: () => {},
//   setThemeAccent: () => {},
// });

// // Utility: resolve effective mode when themeMode === "auto"
// function resolveEffectiveMode(themeMode) {
//   if (themeMode !== "auto") return themeMode;
//   const prefersDark =
//     window.matchMedia &&
//     window.matchMedia("(prefers-color-scheme: dark)").matches;
//   return prefersDark ? "dark" : "light";
// }

// export const ThemeProvider = ({ children }) => {
//   // const repo = useSettingsRepo?.(); // optional; keep working even if not wired
//   const [themeMode, setThemeMode] = useState("auto");
//   const [themeAccent, setThemeAccent] = useState("blue");

//   // Keep an effectiveMode for rendering/classes when in "auto"
//   const [effectiveMode, setEffectiveMode] = useState(() =>
//     resolveEffectiveMode("auto")
//   );

//   // If user changes OS theme while in "auto", keep in sync.
//   useEffect(() => {
//     if (!window.matchMedia) return;
//     const mql = window.matchMedia("(prefers-color-scheme: dark)");
//     const handler = () => setEffectiveMode(resolveEffectiveMode(themeMode));
//     mql.addEventListener?.("change", handler);
//     mql.addListener?.(handler); // older Safari
//     return () => {
//       mql.removeEventListener?.("change", handler);
//       mql.removeListener?.(handler);
//     };
//   }, [themeMode]);

//   // Recompute effectiveMode whenever themeMode changes
//   useEffect(() => {
//     setEffectiveMode(resolveEffectiveMode(themeMode));
//   }, [themeMode]);

//   // Apply to <html> so CSS can pivot immediately
//   useEffect(() => {
//     const el = document.documentElement;
//     el.dataset.theme = effectiveMode; // "light" | "dark"
//     // el.dataset.mode = themeMode; // "light" | "dark" | "auto"
//     el.dataset.themeAccent = themeAccent; // "blue", etc.

//     // (Optional) aid for form styling + browser UI coloring
//     // <meta name="color-scheme"> tells UA supported schemes
//     let colorSchemeMeta = document.querySelector('meta[name="color-scheme"]');
//     if (!colorSchemeMeta) {
//       colorSchemeMeta = document.createElement("meta");
//       colorSchemeMeta.setAttribute("name", "color-scheme");
//       document.head.appendChild(colorSchemeMeta);
//     }
//     colorSchemeMeta.setAttribute(
//       "content",
//       effectiveMode === "dark" ? "dark light" : "light dark"
//     );

//     // (Optional) set <meta name="theme-accent"> to match brand/background
//     // You can map themeAccent/effectiveMode to actual hex if desired.
//     let themeMeta = document.querySelector('meta[name="theme-accent"]');
//     if (!themeMeta) {
//       themeMeta = document.createElement("meta");
//       themeMeta.setAttribute("name", "theme-accent");
//       document.head.appendChild(themeMeta);
//     }
//     // Example neutral fallback; replace with your palette map if you have one
//     themeMeta.setAttribute(
//       "content",
//       effectiveMode === "dark" ? "#0b0b0d" : "#ffffff"
//     );
//   }, [effectiveMode, themeMode, themeAccent]);

//   const value = useMemo(
//     () => ({
//       themeMode, // "light" | "dark" | "auto"
//       themeAccent, // "blue", etc.
//       effectiveMode, // resolved "light" | "dark"
//       setThemeMode, // call from SettingsPage
//       setThemeAccent, // call from SettingsPage
//     }),
//     [themeMode, themeAccent, effectiveMode]
//   );

//   return (
//     <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
//   );
// };

// export const useTheme = () => useContext(ThemeContext);
